import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { OfficeApi } from '@features/business/services/office-api';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { OfficeDetailModel, OfficeCreateRequestModel } from '@features/business/models/office.model';
import { BusinessGroupedModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { FormFooter } from "@shared/components/form-footer/form-footer";
import { AccordionModule } from 'primeng/accordion';
import { CountryService } from '@shared/services/country-service/country-service';
import { Country } from '@shared/models/location.model';
import { BusinessLogo } from "@shared/components/business-logo/business-logo";
import { FlagImage } from "@shared/components/flag-image/flag-image";
import { AppDialog } from "@shared/components/dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { BusinessStateService } from '@core/services/business-state.service';

interface OfficeFormData {
  name: string;
  address: string;
  country: string;
  state: string;
  // city: string;
  isMain: boolean;
}

@Component({
  selector: 'app-office-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    MessageModule,
    StepperModule,
    ToastModule,
    SelectModule,
    FormFooter,
    AccordionModule,
    TooltipModule,
    BusinessLogo,
    FlagImage,
    AppDialog,
    BlockUI,
    ProgressSpinner
],
  templateUrl: './office-form.html',
  styleUrl: './office-form.scss',
  providers: [MessageService]
})
export class OfficeForm {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly messageService = inject(MessageService);
  readonly countryService = inject(CountryService);
  readonly officeApi = inject(OfficeApi);
  readonly businessApi = inject(BusinessApi);
  private readonly businessStateService = inject(BusinessStateService);

  isEditMode = signal(false);
  selectedBusinessId = signal<string>('');

  businessGroupOptions: BusinessGroupedModel[] = [];

  countryOptions: Country[] = [];

  form: FormGroup = new FormGroup({
    businessId: new FormControl('', [Validators.required]),
    offices: new FormArray([])
  });

  officeId: string | null = null;
  isFormSubmitted = false;
  isLoading = false;
  activeStep: number = 1;
  activeAccordion: number | null = 0;

  visibleSuccessDialog: boolean = false;
  dialogTitle: string = '';
  dialogMessage: string = '';

  ngOnInit() {
    this.checkMode();
    this.getBusinessGroupList();
    this.getCountryList();
    this.addOfficeForm(); // Agregar primera sede por defecto
  }

  activeAccordionChange(index : number){
    this.activeAccordion = this.activeAccordion === index ? null : index;
  }

  private checkMode(): void {
    this.officeId = this.route.snapshot.paramMap.get('id');

    if (this.officeId) {
      this.isEditMode.set(true);
      this.loadOfficeData(this.officeId);
    } else {
      this.isEditMode.set(false);
    }
  }

  private loadOfficeData(id: string): void {
    this.isLoading = true;

    this.officeApi.getOfficeById(id).subscribe({
      next: (response: DataEndpoint<OfficeDetailModel>) => {
        if (response.Status === 200) {
          this.setEditForm(response.Data!);
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message,
          life: 3000
        });
        this.isLoading = false;
      }
    });
  }

  private setEditForm(data: OfficeDetailModel) {
    this.form.patchValue({
      businessId: data.BusinessId
    });
    this.selectedBusinessId.set(data.BusinessId);

    // Para edición, solo una sede
    const officesArray = this.form.get('offices') as FormArray;
    officesArray.clear();

    const officeForm = this.createOfficeFormGroup();
    officeForm.patchValue({
      name: data.Name,
      address: data.Address,
      country: data.Country,
      state: data.State,
      // city: data.City,
      isMain: data.IsMain
    });
    officesArray.push(officeForm);
  }

  getBusinessGroupList() {
    this.isLoading = true;

    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  getCountryList() {
    this.countryService.getCountries().subscribe(countries => {
      this.countryOptions = countries;
    });
  }

  get officesFormArray(): FormArray {
    return this.form.get('offices') as FormArray;
  }

  createOfficeFormGroup(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      state: new FormControl(''),
      // city: new FormControl('', [Validators.required]),
      isMain: new FormControl(false)
    });
  }

  addOfficeForm() {
    const officeForm = this.createOfficeFormGroup();
    this.officesFormArray.push(officeForm);
  }

  removeOfficeForm(index: number) {
    if (this.officesFormArray.length > 1) {
      this.officesFormArray.removeAt(index);
    }
  }

  // Validación del formulario antes de avanzar
  canGoNext(): boolean {
    return this.form.valid;
  }

  // Avanzar al siguiente paso
  onClickNextStep(): void {
    if (!this.canGoNext()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.activeStep < 2) {
      this.onSaveForm();
    }
  }

  // Retroceder al paso anterior
  onClickPreviousStep(): void {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  onClickCancel(): void {
    this.router.navigate([Routes.officesPage]);
  }

  // Helpers para deshabilitar botones
  isFirstStep(): boolean {
    return this.activeStep === 1;
  }

  isLastStep(): boolean {
    return this.activeStep === 2;
  }

  onSaveForm() {
    if (this.form.valid) {
      this.isLoading = true;

      const businessId = this.form.get('businessId')?.value;
      const offices = this.officesFormArray.value as OfficeFormData[];

      const request: OfficeCreateRequestModel[] = offices.map(officeData => ({
        Name: officeData.name,
        Address: officeData.address,
        BusinessId: businessId,
        IsMain: officeData.isMain,
        Country: officeData.country,
        State: officeData.state,
        City: '-' // TODO aleyton: por el momento no se usa el campo city
      }));

      if (this.isEditMode()) {
        this.updateOffice(request);
      } else {
        this.createOffices(request);
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'El formulario es inválido. Por favor, revisar.' });
    }
  }

  private createOffices(request: OfficeCreateRequestModel[]) {
    this.officeApi.createOffice(request).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.businessStateService.refreshBusinessList();
          this.showSuccessDialog();
          this.activeStep = 2;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  private updateOffice(request: OfficeCreateRequestModel[]) {
    this.officeApi.updateOffice(this.officeId!, request[0]).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.businessStateService.refreshBusinessList();
          this.activeStep = 2;
          this.showSuccessDialog();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    });
  }

  showSuccessDialog() {
    this.dialogTitle = this.isEditMode()
      ? '¡Sede actualizada!'
      : '¡Sedes creadas!';
    this.dialogMessage = this.isEditMode()
      ? 'La sede ha sido actualizada exitosamente.'
      : 'Las sedes han sido creadas exitosamente.';
    this.visibleSuccessDialog = true;
  }

  onCloseDialog() {
    this.visibleSuccessDialog = false;
    this.router.navigate([Routes.officesPage]);
  }

  isInvalid(controlName: string, formGroupIndex?: number): boolean {
    let control;
    if (formGroupIndex !== undefined) {
      const officeForm = this.officesFormArray.at(formGroupIndex) as FormGroup;
      control = officeForm.get(controlName);
    } else {
      control = this.form.get(controlName);
    }
    return (control?.invalid && (control.touched || this.isFormSubmitted)) ?? false;
  }

  getErrorMessage(controlName: string, formGroupIndex?: number): string {
    let control;
    if (formGroupIndex !== undefined) {
      const officeForm = this.officesFormArray.at(formGroupIndex) as FormGroup;
      control = officeForm.get(controlName);
    } else {
      control = this.form.get(controlName);
    }

    if (control?.hasError('required')) {
      return 'El campo es obligatorio.';
    }
    return '';
  }

  getBusinessOptions() {
    return this.businessGroupOptions.flatMap(group =>
      group.Business?.map(business => ({
        label: business.Name,
        value: business.Id,
        logoUrl: business.LogoUrl
      })) || []
    );
  }

  // Getters para FormControls del FormArray
  getOfficeNameControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('name') as FormControl;
  }

  getOfficeAddressControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('address') as FormControl;
  }

  getOfficeCountryControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('country') as FormControl;
  }

  getOfficeStateControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('state') as FormControl;
  }

  // getOfficeCityControl(index: number): FormControl {
  //   return this.officesFormArray.at(index).get('city') as FormControl;
  // }

  getOfficeIsMainControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('isMain') as FormControl;
  }

  getOfficeName(index: number): string {
    const officeName = this.officesFormArray.at(index).get('name')?.value || '(Sin nombre)';
    return `${index + 1} - ${officeName}`;
  }
}
